import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { Close, Refresh, Search } from '@mui/icons-material';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageContainer, PageHeader, SectionCard, StatusChip, TabShell, EmptyState, InlineMessage } from '../components/ui';
import { ResponsiveRows, type ResponsiveRowsColumn } from '../components/ui/ResponsiveRows';
import { useToast } from '../contexts/ToastContext';
import { useSearchParams } from 'react-router-dom';
import { useUrlTab } from '../hooks/useUrlTab';
import { tokens } from '../theme/tokens';
import {
  syncRunKindChipLabel,
  syncRunKindLabel,
  syncRunKindTone,
  syncRunUpdatedNoun,
  type SyncRunKind,
} from '../utils/syncRunKind';
import { fetchStoreSyncRows, fetchStoreSyncSummary } from './adminStoreSync/api';
import { HealthOverview } from './adminStoreSync/HealthOverview';
import { RunStrip } from './adminStoreSync/RunStrip';
import { StoreSyncDrawer } from './adminStoreSync/StoreSyncDrawer';
import {
  TILE_COPY,
  formatAgo,
  formatDay,
  formatDuration,
  formatWhen,
  nextRunText,
  platformLabel,
  presentStatus,
  statusSubline,
  type StoreSyncFrequency,
  type StoreSyncRow,
  type StoreSyncTile,
} from './adminStoreSync/model';

const PAGE_SIZE = 25;
const SCOPES = ['linked', 'unlinked'] as const;
type Scope = (typeof SCOPES)[number];
type Sort = 'attention' | 'name' | 'updated';

const COLUMNS: ResponsiveRowsColumn[] = [
  { key: 'store', header: 'Store', width: 'minmax(220px, 1.7fr)' },
  { key: 'status', header: 'Status', width: '140px', mobileLabel: true },
  { key: 'last', header: 'Last run', width: 'minmax(150px, 1fr)', mobileLabel: true },
  { key: 'updated', header: 'Updated', width: '90px', align: 'end', mobileLabel: true },
  { key: 'issues', header: 'Issues', width: '130px', mobileLabel: true },
  { key: 'strip', header: 'Last 14 runs', width: '100px', mobileLabel: true },
  { key: 'next', header: 'Next run', width: '120px', mobileLabel: true },
  { key: 'success', header: 'Last success', width: '96px', mobileLabel: true },
];

const selectSx = (active: boolean) => ({
  minWidth: 150,
  '& .MuiOutlinedInput-root': {
    height: 34,
    fontSize: 13,
    bgcolor: active ? tokens.accent.tint : undefined,
    '& fieldset': { borderColor: active ? tokens.accent.border : undefined },
  },
});

export const AdminStoreSyncOverviewPage: React.FC = () => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [scope, setScope] = useUrlTab<Scope>(SCOPES, 'linked', 'scope');
  const [status, setStatus] = useState<StoreSyncTile | null>(null);
  // `?org=<organizationId>`: the Stores page opens this view for one store; search matches the org id.
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(() => searchParams.get('org') ?? '');
  const [debouncedQ, setDebouncedQ] = useState(() => searchParams.get('org') ?? '');
  const [platform, setPlatform] = useState('');
  const [schedule, setSchedule] = useState<StoreSyncFrequency | ''>('');
  const [kind, setKind] = useState<SyncRunKind | ''>('');
  const [sort, setSort] = useState<Sort>('attention');
  // The page is tied to the filters it was chosen under, so a filter change is page one on the very next render.
  const [pageState, setPageState] = useState<{ key: string; page: number }>({ key: '', page: 1 });
  const [open, setOpen] = useState<StoreSyncRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const filterKey = JSON.stringify([scope, status, debouncedQ, platform, schedule, kind, sort]);
  const page = pageState.key === filterKey ? pageState.page : 1;
  const setPage = (next: number | ((p: number) => number)) =>
    setPageState({ key: filterKey, page: typeof next === 'function' ? next(page) : next });

  const summaryQuery = useQuery({ queryKey: ['admin-store-sync', 'summary'], queryFn: () => fetchStoreSyncSummary() });
  const listQuery = useQuery({
    queryKey: ['admin-store-sync', 'rows', scope, status, debouncedQ, platform, schedule, kind, sort, page],
    queryFn: () =>
      fetchStoreSyncRows({
        scope,
        status: scope === 'linked' ? status ?? undefined : undefined,
        q: debouncedQ,
        platform: platform || undefined,
        schedule: schedule || undefined,
        kind: kind || undefined,
        sort,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const summary = summaryQuery.data;
  const rows = listQuery.data?.rows ?? [];
  const total = listQuery.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);

  const refresh = async () => {
    try {
      // The summary read rebuilds the server snapshot; the rows are read after it, so both agree.
      const fresh = await fetchStoreSyncSummary(true);
      queryClient.setQueryData(['admin-store-sync', 'summary'], fresh);
      await queryClient.refetchQueries({ queryKey: ['admin-store-sync', 'rows'], type: 'active' });
      if (queryClient.getQueryState(['admin-store-sync', 'rows', scope, status, debouncedQ, platform, schedule, kind, sort, page])?.status === 'error') {
        throw new Error('Could not reload the table.');
      }
      showToast('Refreshed from latest run summaries', 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not refresh.', 'error');
    }
  };

  const onTile = (tile: StoreSyncTile | null) => {
    setStatus(tile);
    if (tile) setScope('linked');
  };

  const scopeTabs = useMemo(
    () => [
      {
        key: 'linked' as const,
        label: (
          <span>
            Linked {summary ? <CountPill value={summary.linked} /> : null}
          </span>
        ),
      },
      {
        key: 'unlinked' as const,
        label: (
          <span>
            Unlinked {summary ? <CountPill value={summary.unlinked} caution={summary.unlinked > 0} /> : null}
          </span>
        ),
      },
    ],
    [summary],
  );

  const filtersActive = Boolean(status || debouncedQ || platform || schedule || kind);

  const renderCell = (row: StoreSyncRow, key: string): React.ReactNode => {
    const st = presentStatus(row.status);
    const runKind: SyncRunKind = row.lastRun?.kind ?? 'catalog';
    const body = (() => {
      switch (key) {
        case 'store':
          return (
            <Box sx={{ minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => setOpen(row)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'block',
                  maxWidth: '100%',
                  fontSize: 13.5,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '&:focus-visible': { outline: `2px solid ${tokens.accent.main}`, outlineOffset: 2 },
                }}
              >
                {row.name}
              </Box>
              <Typography noWrap sx={{ fontSize: 12, color: tokens.ink.muted }}>
                <Box component="span" sx={{ fontWeight: 600, color: tokens.ink.bodyAlt }}>
                  {platformLabel(row.platform)}
                </Box>
                {row.organizationName && row.organizationName !== row.name ? ` · ${row.organizationName}` : ''}
                {row.domain ? ` · ${row.domain}` : ''}
                {row.orgIssue === 'missing' && row.organizationId ? ` · ${row.organizationId}` : ''}
              </Typography>
              {row.orgIssue && (
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip
                    label={row.orgIssue === 'none' ? 'No organization' : 'Org not found'}
                    tone={row.orgIssue === 'none' ? 'caution' : 'problem'}
                  />
                </Box>
              )}
            </Box>
          );
        case 'status': {
          const sub = statusSubline(row, syncRunKindLabel(runKind));
          return (
            <Box>
              <StatusChip label={st.label} tone={st.tone} dot sx={{ height: 22 }} />
              {sub && <Typography sx={{ fontSize: 11.5, color: tokens.ink.muted, mt: 0.25 }}>{sub}</Typography>}
            </Box>
          );
        }
        case 'last':
          return row.lastRun ? (
            <Box>
              <Typography sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>{formatWhen(row.lastRun.at)}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                <StatusChip label={syncRunKindChipLabel(runKind)} tone={syncRunKindTone(runKind)} sx={{ height: 18, fontSize: 11 }} />
                {row.lastRun.durationMs !== null && (
                  <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>{formatDuration(row.lastRun.durationMs)}</Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: tokens.ink.faint }}>{row.lastSyncAt ? formatDay(row.lastSyncAt) : '—'}</Typography>
          );
        case 'updated':
          return row.lastRun && row.lastRun.updated !== null ? (
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {row.lastRun.updated.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.ink.faint }}>{syncRunUpdatedNoun(runKind)}</Typography>
            </Box>
          ) : (
            <Typography sx={{ color: tokens.ink.faint }}>—</Typography>
          );
        case 'issues':
          if (!row.lastRun) return <Typography sx={{ color: tokens.ink.faint }}>—</Typography>;
          if (row.lastRun.errors === 0 && row.lastRun.warnings === 0)
            return <Typography sx={{ fontSize: 12.5, color: tokens.ink.faint }}>None</Typography>;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {row.lastRun.errors > 0 && (
                <StatusChip label={`${row.lastRun.errors} ${row.lastRun.errors === 1 ? 'error' : 'errors'}`} tone="problem" sx={{ height: 20, fontSize: 11.5 }} />
              )}
              {row.lastRun.warnings > 0 && (
                <StatusChip label={`${row.lastRun.warnings} ${row.lastRun.warnings === 1 ? 'warning' : 'warnings'}`} tone="caution" sx={{ height: 20, fontSize: 11.5 }} />
              )}
            </Box>
          );
        case 'strip':
          return <RunStrip row={row} />;
        case 'next': {
          const n = nextRunText(row);
          return (
            <Typography
              sx={{
                fontSize: 12.5,
                color: n.tone === 'overdue' ? tokens.signal.caution.ink : n.tone === 'quiet' ? tokens.ink.faint : tokens.ink.body,
              }}
            >
              {n.text}
            </Typography>
          );
        }
        default: {
          const bad = row.status === 'failing' || row.status === 'missed';
          return (
            <Typography sx={{ fontSize: 12.5, color: bad ? tokens.signal.problem.ink : tokens.ink.body }}>
              {row.lastSuccessAt ? formatDay(row.lastSuccessAt) : '—'}
            </Typography>
          );
        }
      }
    })();
    // The whole row opens the drawer; the store name is the keyboard target.
    return (
      <Box onClick={() => setOpen(row)} sx={{ cursor: 'pointer', minWidth: 0 }}>
        {body}
      </Box>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Store ERP sync"
        description="Health of every retailer integration, from each sync's run summary. Click a store for run history, warnings and actions."
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {summary && (
              <Typography sx={{ fontSize: 12, color: tokens.ink.faint }}>Updated {formatAgo(summary.generatedAt)}</Typography>
            )}
            <Button
              startIcon={<Refresh />}
              variant="outlined"
              onClick={refresh}
              disabled={summaryQuery.isFetching || listQuery.isFetching}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>
        }
      />

      {(summaryQuery.isError || listQuery.isError) && (
        <InlineMessage severity="error">
          Could not load store sync data.{' '}
          <Button
            size="small"
            onClick={() => {
              summaryQuery.refetch();
              listQuery.refetch();
            }}
            sx={{ textTransform: 'none' }}
          >
            Try again
          </Button>
        </InlineMessage>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <HealthOverview summary={summary} loading={summaryQuery.isLoading} activeTile={status} onTile={onTile} />

        <Box>
          <TabShell tabs={scopeTabs} value={scope} onChange={setScope} sticky={false} aria-label="Integration scope">
            <SectionCard dense>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, p: '12px 16px' }}>
                <TextField
                  size="small"
                  placeholder="Search store, organization or domain"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  sx={{ flex: '1 1 240px', maxWidth: 360, '& .MuiOutlinedInput-root': { height: 34, fontSize: 13 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ 'aria-label': 'Search integrations' }}
                />
                <TextField select size="small" value={platform} onChange={(e) => setPlatform(e.target.value)} sx={selectSx(Boolean(platform))} SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': 'Platform' }}>
                  <MenuItem value="">Platform: All</MenuItem>
                  <MenuItem value="shopify">Shopify</MenuItem>
                  <MenuItem value="bigcommerce">BigCommerce</MenuItem>
                  <MenuItem value="lightspeed">Lightspeed</MenuItem>
                </TextField>
                <TextField select size="small" value={schedule} onChange={(e) => setSchedule(e.target.value as StoreSyncFrequency | '')} sx={selectSx(Boolean(schedule))} SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': 'Schedule' }}>
                  <MenuItem value="">Schedule: Any</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </TextField>
                <TextField select size="small" value={kind} onChange={(e) => setKind(e.target.value as SyncRunKind | '')} sx={selectSx(Boolean(kind))} SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': 'Run type' }}>
                  <MenuItem value="">Run type: Any</MenuItem>
                  <MenuItem value="pricing">Pricing only</MenuItem>
                  <MenuItem value="compare">Compare &amp; update</MenuItem>
                  <MenuItem value="full">Full catalog</MenuItem>
                  <MenuItem value="catalog">Catalog</MenuItem>
                </TextField>
                <TextField select size="small" value={sort} onChange={(e) => setSort(e.target.value as Sort)} sx={selectSx(sort !== 'attention')} inputProps={{ 'aria-label': 'Sort' }}>
                  <MenuItem value="attention">Sort: Needs attention</MenuItem>
                  <MenuItem value="name">Sort: Store A–Z</MenuItem>
                  <MenuItem value="updated">Sort: Most updated</MenuItem>
                </TextField>
                {status && scope === 'linked' && (
                  <StatusChip
                    label={`${TILE_COPY[status].label}`}
                    tone="info"
                    variant="outlined"
                    icon={<Close fontSize="small" onClick={() => setStatus(null)} aria-label="Clear tile filter" />}
                  />
                )}
                <Typography sx={{ ml: 'auto', fontSize: 12.5, color: tokens.ink.muted }}>
                  {total.toLocaleString()} integrations
                </Typography>
              </Box>

              <Box sx={{ overflowX: 'auto' }}>
                <Box sx={{ minWidth: { md: 1140 } }}>
                  <ResponsiveRows
                    columns={COLUMNS}
                    rows={rows}
                    getRowKey={(r) => r.integrationId}
                    renderCell={renderCell}
                    isLoading={listQuery.isLoading}
                    skeletonRows={6}
                    padding="11px 16px"
                    headerPadding="9px 16px"
                    headerSx={{ bgcolor: tokens.surface.subtle }}
                    rowSx={(r) => ({ ...(open?.integrationId === r.integrationId ? { bgcolor: tokens.signal.info.row } : {}), '&:hover': { bgcolor: tokens.surface.sunken } })}
                    emptyState={
                      <EmptyState
                        title={
                          scope === 'unlinked'
                            ? 'No unlinked integrations.'
                            : filtersActive
                              ? 'No integrations match these filters.'
                              : 'No store integrations yet.'
                        }
                      />
                    }
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: '10px 16px', borderTop: `1px solid ${tokens.line.hairline}` }}>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>
                  Showing {from}–{to} of {total.toLocaleString()}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" color="inherit" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} sx={{ textTransform: 'none' }}>
                    Previous
                  </Button>
                  <Button size="small" variant="outlined" color="inherit" disabled={page >= lastPage} onClick={() => setPage((p) => Math.min(lastPage, p + 1))} sx={{ textTransform: 'none' }}>
                    Next
                  </Button>
                </Box>
              </Box>
            </SectionCard>
          </TabShell>
        </Box>
      </Box>

      <StoreSyncDrawer row={open} onClose={() => setOpen(null)} />
    </PageContainer>
  );
};

const CountPill: React.FC<{ value: number; caution?: boolean }> = ({ value, caution = false }) => {
  const palette = caution ? tokens.signal.caution : tokens.signal.neutral;
  return (
    <Box
      component="span"
      sx={{
        ml: 0.5,
        px: 0.9,
        height: 18,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: `${tokens.radius.pill}px`,
        fontSize: 11.5,
        fontWeight: 600,
        bgcolor: palette.tint,
        color: palette.ink,
      }}
    >
      {value.toLocaleString()}
    </Box>
  );
};

export default AdminStoreSyncOverviewPage;
